import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Card, ProgressBar, Button, ListGroup, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTrash,
  faFileAlt,
  faCloudUploadAlt
} from '@fortawesome/free-solid-svg-icons';
import PropTypes from 'prop-types';

const FileUploadDropzone = ({
  onFilesChange,
  maxFiles = 10,
  maxSize = 10485760
}) => {
  const [files, setFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});

  const onDrop = useCallback(
    acceptedFiles => {
      const newFiles = acceptedFiles.map(file => ({
        file,
        id: Math.random().toString(36).substr(2, 9),
        name: file.name,
        size: file.size,
        status: 'ready'
      }));

      const updatedFiles = [...files, ...newFiles].slice(0, maxFiles);
      setFiles(updatedFiles);

      if (onFilesChange) {
        onFilesChange(updatedFiles);
      }

      // Simular progresso de upload para cada arquivo
      newFiles.forEach(fileObj => {
        simulateUploadProgress(fileObj.id);
      });
    },
    [files, maxFiles, onFilesChange]
  );

  const simulateUploadProgress = fileId => {
    let progress = 0;
    const interval = setInterval(() => {
      progress += Math.random() * 30;
      if (progress >= 100) {
        progress = 100;
        clearInterval(interval);
        setUploadProgress(prev => ({ ...prev, [fileId]: 100 }));
      } else {
        setUploadProgress(prev => ({ ...prev, [fileId]: progress }));
      }
    }, 200);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles,
    maxSize,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
        '.xlsx'
      ],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        ['.docx'],
      'image/*': ['.png', '.jpg', '.jpeg', '.gif'],
      'text/csv': ['.csv']
    }
  });

  const removeFile = fileId => {
    const updatedFiles = files.filter(f => f.id !== fileId);
    setFiles(updatedFiles);
    setUploadProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[fileId];
      return newProgress;
    });

    if (onFilesChange) {
      onFilesChange(updatedFiles);
    }
  };

  const formatFileSize = bytes => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div>
      <Card
        {...getRootProps()}
        className={`text-center mb-3 ${
          isDragActive ? 'border-primary bg-light' : 'border-dashed'
        }`}
        style={{
          cursor: 'pointer',
          borderStyle: 'dashed',
          borderWidth: '2px',
          padding: '2rem',
          transition: 'all 0.3s ease'
        }}
      >
        <input {...getInputProps()} />
        <Card.Body>
          <FontAwesomeIcon
            icon={faCloudUploadAlt}
            className="text-primary mb-3"
            size="3x"
          />
          {isDragActive ? (
            <p className="mb-0 text-primary">Solte os arquivos aqui...</p>
          ) : (
            <>
              <h5 className="mb-2">Arraste arquivos aqui</h5>
              <p className="text-muted mb-0">
                ou clique para selecionar arquivos
              </p>
              <small className="text-muted">
                (Máximo {maxFiles} arquivos, até {formatFileSize(maxSize)} cada)
              </small>
            </>
          )}
        </Card.Body>
      </Card>

      {files.length > 0 && (
        <Card>
          <Card.Header>
            <h6 className="mb-0">
              Arquivos Selecionados ({files.length}/{maxFiles})
            </h6>
          </Card.Header>
          <ListGroup variant="flush">
            {files.map(fileObj => (
              <ListGroup.Item key={fileObj.id}>
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div className="d-flex align-items-center">
                    <FontAwesomeIcon
                      icon={faFileAlt}
                      className="text-secondary me-2"
                    />
                    <div>
                      <div className="fw-semibold">{fileObj.name}</div>
                      <small className="text-muted">
                        {formatFileSize(fileObj.size)}
                      </small>
                    </div>
                  </div>
                  <div className="d-flex align-items-center">
                    {uploadProgress[fileObj.id] === 100 && (
                      <Badge bg="success" className="me-2">
                        Completo
                      </Badge>
                    )}
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => removeFile(fileObj.id)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </Button>
                  </div>
                </div>
                {uploadProgress[fileObj.id] !== undefined &&
                  uploadProgress[fileObj.id] < 100 && (
                    <ProgressBar
                      now={uploadProgress[fileObj.id]}
                      label={`${Math.round(uploadProgress[fileObj.id])}%`}
                      animated
                      striped
                      variant="primary"
                    />
                  )}
              </ListGroup.Item>
            ))}
          </ListGroup>
        </Card>
      )}
    </div>
  );
};

FileUploadDropzone.propTypes = {
  onFilesChange: PropTypes.func,
  maxFiles: PropTypes.number,
  maxSize: PropTypes.number
};

export default FileUploadDropzone;
